import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { Channel } from '@src/api/shared/community/channelApiClient';
import { chatApiClient, ThreadChat } from '@src/api/shared/community/chatApiClient';
import { POST_SEND_POLL_DELAY } from '@src/features/shared/community/constants/polling';
import type { Attachment } from '@src/idb';
import { AppDispatch } from '@src/store/store';
import { clearThreadData, fetchThreads } from '@src/store/threadSlice';
import logger from '@src/utils/logger';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

interface UseThreadHandlersParams {
  channel: Channel | null;
  activeThreadParentId: number | null;
  parentChat?: ThreadChat;
  threadReplies: ThreadChat[];
  setParentChat: (parentChat: ThreadChat | undefined) => void;
  setThreadReplies: (replies: ThreadChat[]) => void;
  setThreadEditingChatId: (id: number | null) => void;
  setThreadEditingMessage: (message: string) => void;
  setThreadEditingAttachments: (attachments: Attachment[]) => void;
  setThreadIsAnonymous: (isAnonymous: boolean) => void;
  setActiveThreadParentId: (id: number | null) => void;
  setIsThreadOpen: (isOpen: boolean) => void;
  setInitialThreadChatId?: (id: number | undefined) => void;
  setFirstUnreadThreadChatId?: (id: number | null) => void;
}

interface UseThreadHandlersResult {
  handleOpenThread: (chatId: number, scrollToChatId?: number) => Promise<void>;
  handleCloseThread: () => void;
  handleEditThreadReply: (reply: ThreadChat) => void;
  handleDeleteThreadReply: (replyId: number) => Promise<void>;
  handleThreadChatDelete: (chat: ThreadChat) => void;
  handleThreadReplySent: () => Promise<void>;
}

export const useThreadHandlers = (params: UseThreadHandlersParams): UseThreadHandlersResult => {
  const {
    channel,
    activeThreadParentId,
    parentChat,
    threadReplies,
    setParentChat,
    setThreadReplies,
    setActiveThreadParentId,
    setIsThreadOpen,
    setInitialThreadChatId,
    setFirstUnreadThreadChatId,
    setThreadEditingChatId,
    setThreadEditingMessage,
    setThreadEditingAttachments,
    setThreadIsAnonymous,
  } = params;

  const dispatch = useDispatch<AppDispatch>();

  /**
   * スレッドを閉じる処理
   * 全てのスレッド関連データをリセットする
   */
  const handleCloseThread = useCallback(() => {
    setIsThreadOpen(false);
    setThreadReplies([]);
    setActiveThreadParentId(null);
    if (setInitialThreadChatId) {
      setInitialThreadChatId(undefined);
    }
    if (setFirstUnreadThreadChatId) {
      setFirstUnreadThreadChatId(null);
    }
  }, [
    setIsThreadOpen,
    setThreadReplies,
    setActiveThreadParentId,
    setInitialThreadChatId,
    setFirstUnreadThreadChatId,
  ]);

  /**
   * スレッドデータをリフレッシュする共通処理
   */
  const refreshThreadData = useCallback(async () => {
    if (!activeThreadParentId) {
      return;
    }

    const result = await dispatch(
      fetchThreads({
        channel,
        parentChatId: activeThreadParentId,
        markAsRead: true,
      }),
    );

    if (fetchThreads.fulfilled.match(result) && result.payload) {
      const data = result.payload;
      setParentChat(data);
      setThreadReplies(data.threads);
    }
  }, [activeThreadParentId, channel, dispatch, setParentChat, setThreadReplies]);

  const handleOpenThread = useCallback(
    async (chatId: number, scrollToChatId?: number) => {
      dispatch(clearThreadData());
      setParentChat(undefined);
      setActiveThreadParentId(chatId);
      setIsThreadOpen(true);

      try {
        const result = await dispatch(
          fetchThreads({
            channel,
            parentChatId: chatId,
            markAsRead: false,
          }),
        );

        if (fetchThreads.fulfilled.match(result) && result.payload) {
          const data = result.payload;
          setParentChat(data);
          setThreadReplies(data.threads);

          // 未読スレッドチャットIDはReduxから自動的に同期される
          if (setFirstUnreadThreadChatId) {
            setFirstUnreadThreadChatId(data.first_unread_thread_chat_id);
          }

          // スレッドデータ取得後、スクロール対象のチャットIDを設定
          if (scrollToChatId && setInitialThreadChatId) {
            setInitialThreadChatId(scrollToChatId);
          }
        } else {
          logger.error('スレッドの取得に失敗しました');
          setParentChat(undefined);
          setThreadReplies([]);
        }
      } catch (error) {
        logger.error('スレッド取得中にエラーが発生しました:', error);
        setParentChat(undefined);
        setThreadReplies([]);
      }
    },
    [
      channel,
      dispatch,
      setActiveThreadParentId,
      setIsThreadOpen,
      setParentChat,
      setThreadReplies,
      setInitialThreadChatId,
      setFirstUnreadThreadChatId,
    ],
  );

  const handleEditThreadReply = useCallback(
    (reply: ThreadChat) => {
      setThreadEditingChatId(reply.id);
      setThreadEditingMessage(reply.message);
      setThreadEditingAttachments(
        (reply.attachments ?? []).map((chatAttachment) => ({
          name: chatAttachment.file_name,
          type: chatAttachment.extension,
          size: 0,
          blob: new Blob(),
          chatAttachment: chatAttachment,
        })),
      );
      setThreadIsAnonymous(reply.is_anonymous);
    },
    [
      setThreadEditingChatId,
      setThreadEditingMessage,
      setThreadEditingAttachments,
      setThreadIsAnonymous,
    ],
  );

  const handleDeleteThreadReply = useCallback(
    async (replyId: number) => {
      if (!activeThreadParentId) {
        return;
      }

      const isParentDeleted = parentChat?.is_trash_displayed ?? false;
      const willBeEmpty = threadReplies.length === 1; // 削除するとゼロになる
      const shouldCloseThread = isParentDeleted && willBeEmpty;

      if (shouldCloseThread) {
        // スレッドを閉じる場合は、削除リクエスト前にポーリングを停止
        setIsThreadOpen(false);
      }

      try {
        const result = await chatApiClient.delete(channel?.code ?? '', replyId);
        handleApiResponse(
          result,
          async () => {
            if (!shouldCloseThread) {
              await refreshThreadData();
              return;
            }
            handleCloseThread();
          },
          (error) => {
            logger.error('Failed to delete thread reply:', error);
            if (shouldCloseThread) {
              setIsThreadOpen(true);
            }
          },
        );
      } catch (error) {
        logger.error('Error deleting thread reply:', error);
        if (shouldCloseThread) {
          setIsThreadOpen(true);
        }
      }
    },
    [
      activeThreadParentId,
      channel,
      parentChat,
      threadReplies,
      refreshThreadData,
      handleCloseThread,
      setIsThreadOpen,
    ],
  );

  const handleThreadChatDelete = useCallback(
    (chat: ThreadChat) => {
      handleDeleteThreadReply(chat.id);
    },
    [handleDeleteThreadReply],
  );

  const refreshThreadDataRef = useRef(refreshThreadData);
  useEffect(() => {
    refreshThreadDataRef.current = refreshThreadData;
  }, [refreshThreadData]);

  /**
   * 返信メッセージの投稿処理
   * スレッドデータのリフレッシュのみを行う
   * (CommunityTiptapEditorが既にparent_chat_idを含めて送信処理を行っているため)
   */
  const handleThreadReplySent = useCallback(async () => {
    await refreshThreadData();
    setTimeout(() => void refreshThreadDataRef.current(), POST_SEND_POLL_DELAY);
  }, [refreshThreadData]);

  return {
    handleOpenThread,
    handleCloseThread,
    handleDeleteThreadReply,
    handleThreadChatDelete,
    handleThreadReplySent,
    handleEditThreadReply,
  };
};
